import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaSpinner,
  FaMapMarkerAlt,
  FaClock,
  FaCheckCircle,
  FaRegCircle,
} from "react-icons/fa";
import ModalB from "react-bootstrap/Modal";
import useAuth from "../../hooks/useAuthContext";
import useApp from "../../hooks/useAppContext";
import api from "../../services/axios";
import { formatarDia, formatarHora } from "../../services/conversaoTimer";
import DossieConvidadoSheet from "../adm/DossieConvidadoSheet";
import "../adm/admPatrocinadoresList.css";
import "./agenda.css";

const LABELS_TIPO = {
  geral: "Geral",
  conteudo_patrocinado: "Conteúdo patrocinado",
  conteudo_editorial: "Conteúdo editorial",
  esporte: "Esporte",
  reuniao_negocio: "Reunião de negócio",
  extraordinaria: "Extraordinária",
  acompanhante: "Acompanhante",
  kids: "Kids",
  m_m: "Match & Meet",
  networking: "Networking",
};

const TIPOS_REUNIAO = ["reuniao_negocio", "m_m"];

function idUsuarioAp(ap) {
  return Number(ap?.id_usuario ?? ap?.idUsuario);
}

function horarioCurto(valor) {
  if (!valor) return "—";
  const formatado = formatarHora(valor);
  return formatado ? formatado.substring(0, 5) : "—";
}

function extrairDataIso(dtHoraInicio) {
  if (!dtHoraInicio) return null;
  const texto = String(dtHoraInicio);
  const match = texto.match(/(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function getOutrosParticipantes(agenda, userId) {
  const idNum = parseInt(userId, 10);
  return (agenda?.participantes || []).filter(
    (ap) => idUsuarioAp(ap) !== idNum
  );
}

function ehTipoReuniao(tipo) {
  return TIPOS_REUNIAO.includes(tipo);
}

function montarConvidadoDossie(usuarioConvidado, handleImagemParticipante) {
  if (!usuarioConvidado) return null;
  return {
    ...usuarioConvidado,
    avatar:
      usuarioConvidado.avatar ||
      handleImagemParticipante?.(usuarioConvidado) ||
      null,
  };
}

function detalheLista(item, usuarioLogado, idUsuarioRef) {
  const agenda = item?.agenda;
  if (!agenda) return "";

  if (ehTipoReuniao(agenda.tipo)) {
    if (agenda.tipo === "m_m") {
      return "Esta reunião será realizada na plenária!";
    }
    const outros = getOutrosParticipantes(agenda, idUsuarioRef || usuarioLogado?.id);
    const convidado = outros[0]?.usuario;
    if (convidado) {
      const partes = [convidado.cargo, convidado.empresa].filter(Boolean);
      return partes.join(" · ") || convidado.nome || "";
    }
    return agenda.local || "Sala de reuniões";
  }

  if (
    agenda.tipo === "conteudo_patrocinado" ||
    agenda.tipo === "conteudo_editorial"
  ) {
    return agenda?.patrocinador?.patrocinador
      ? `Patrocinado por: ${agenda.patrocinador.patrocinador}`
      : "";
  }

  if (agenda.tipo === "networking") {
    return agenda.local || "Centro de Convenções Arena Sauípe";
  }

  return "";
}

function ehNetworking(tipo) {
  return tipo === "networking";
}

const TIPOS_COM_DOSSIE = ["patrocinador", "participante_patrocinador"];

function podeVerDossieConvidado(usuario) {
  return TIPOS_COM_DOSSIE.includes(usuario?.tipo);
}

function ehUsuarioConvidado(usuario) {
  return usuario?.tipo === "convidado";
}

function dadosPatrocinadorAgenda(agenda) {
  const p = agenda?.patrocinador;
  if (!p) return null;
  const nome = p.patrocinador || p.nome || null;
  const logo = p.logo || null;
  if (!nome && !logo) return null;
  return { nome, logo };
}

function outroParticipanteReuniao(agenda, idUsuarioRef, preferirTipo) {
  const outros = getOutrosParticipantes(agenda, idUsuarioRef);
  if (preferirTipo) {
    const preferido = outros.find((ap) => ap?.usuario?.tipo === preferirTipo);
    if (preferido?.usuario) return preferido.usuario;
  }
  return outros[0]?.usuario || null;
}

const TIPOS_COM_STATUS = [
  "conteudo_patrocinado",
  "conteudo_editorial",
  "reuniao_negocio",
  "m_m",
];

function exibeStatusPresenca(tipo) {
  return TIPOS_COM_STATUS.includes(tipo);
}

function chaveItemAgenda(item, agenda) {
  if (item?.virtual || ehNetworking(agenda?.tipo)) {
    return String(item?.id || agenda?.id || `networking-${agenda?.id_slot}`);
  }
  return `${item.id}-${agenda.id}`;
}

const Agenda = () => {
  const { usuario } = useAuth();
  const { getDiasEvento, getPalestrantes, handleImagemParticipante } = useApp();

  const [diasEvento, setDiasEvento] = useState([]);
  const [diaSelecionado, setDiaSelecionado] = useState(null);
  const [itens, setItens] = useState([]);
  const [idUsuarioAgenda, setIdUsuarioAgenda] = useState(null);
  const [palestrantes, setPalestrantes] = useState([]);
  const [palestrantesCarregados, setPalestrantesCarregados] = useState(false);
  const [isLoadingDias, setIsLoadingDias] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [itemModal, setItemModal] = useState(null);
  const [convidadoDossie, setConvidadoDossie] = useState(null);
  const [isLoadingDossie, setIsLoadingDossie] = useState(false);
  const cacheDossieRef = useRef({});

  const carregarDias = useCallback(async () => {
    setIsLoadingDias(true);
    try {
      const dados = await getDiasEvento();
      const dias = dados?.dias || [];
      setDiasEvento(dias);

      if (dias.length > 0) {
        const hoje = new Date();
        const hojeIso = [
          hoje.getFullYear(),
          String(hoje.getMonth() + 1).padStart(2, "0"),
          String(hoje.getDate()).padStart(2, "0"),
        ].join("-");
        const diaHoje = dias.find((d) => d.data === hojeIso);
        setDiaSelecionado((atual) => atual || (diaHoje || dias[0]).dia);
      }

      return dias;
    } catch (err) {
      console.error("Erro ao carregar dias do evento:", err);
      setError("Erro ao carregar dias do evento");
      return [];
    } finally {
      setIsLoadingDias(false);
    }
  }, [getDiasEvento]);

  const carregarPalestrantes = useCallback(async () => {
    if (palestrantesCarregados) return;
    try {
      const palestrantesAux = await getPalestrantes();
      setPalestrantes(palestrantesAux || []);
    } catch (err) {
      console.error("Erro ao carregar palestrantes:", err);
    } finally {
      setPalestrantesCarregados(true);
    }
  }, [getPalestrantes, palestrantesCarregados]);

  const carregarAgenda = useCallback(async () => {
    if (!usuario?.id || !diaSelecionado) return;

    setIsLoading(true);
    setError(null);

    try {
      const responseItens = await api.post("/agenda-usuario-list", {
        dia: diaSelecionado,
      });

      const lista =
        responseItens.status === 200 ? responseItens.data.dados || [] : [];
      const idRef =
        responseItens.data?.id_usuario_agenda ?? usuario.id;

      setIdUsuarioAgenda(Number(idRef) || usuario.id);
      setItens(lista);
    } catch (err) {
      console.error("Erro ao carregar minha agenda:", err);
      setError("Erro ao carregar os eventos da sua agenda");
      setItens([]);
    } finally {
      setIsLoading(false);
    }
  }, [usuario, diaSelecionado]);

  useEffect(() => {
    carregarDias();
  }, [carregarDias]);

  useEffect(() => {
    carregarPalestrantes();
  }, [carregarPalestrantes]);

  useEffect(() => {
    carregarAgenda();
  }, [carregarAgenda]);

  const diaAtivoLabel = useMemo(() => {
    const encontrado = diasEvento.find((item) => item.dia === diaSelecionado);
    return encontrado?.label || diaSelecionado || "—";
  }, [diasEvento, diaSelecionado]);

  const carregarDossieConvidado = useCallback(
    async (convidadoBase) => {
      if (!convidadoBase?.id) {
        setConvidadoDossie(null);
        return;
      }

      const cacheKey = String(convidadoBase.id);
      if (cacheDossieRef.current[cacheKey]) {
        setConvidadoDossie(cacheDossieRef.current[cacheKey]);
        return;
      }

      setIsLoadingDossie(true);
      try {
        const resp = await api.get(`/usuarios-dossie/${convidadoBase.id}`);
        const dados = resp.data?.dados || convidadoBase;
        const montado = montarConvidadoDossie(dados, handleImagemParticipante);
        cacheDossieRef.current[cacheKey] = montado;
        setConvidadoDossie(montado);
      } catch (err) {
        console.error("Erro ao carregar dossiê do convidado:", err);
        setConvidadoDossie(
          montarConvidadoDossie(convidadoBase, handleImagemParticipante)
        );
      } finally {
        setIsLoadingDossie(false);
      }
    },
    [handleImagemParticipante]
  );

  const usuarioPodeVerDossie = podeVerDossieConvidado(usuario);

  const abrirModal = (item) => {
    setItemModal(item);
    setShowModal(true);
    setConvidadoDossie(null);

    const agenda = item?.agenda;
    if (ehNetworking(agenda?.tipo) || !ehTipoReuniao(agenda?.tipo)) return;

    const idRef = idUsuarioAgenda || usuario?.id;
    const outros = getOutrosParticipantes(agenda, idRef);
    const convidado =
      outros.find((ap) => ap?.usuario?.tipo === "convidado")?.usuario ||
      outros[0]?.usuario ||
      null;

    if (convidado) {
      setConvidadoDossie(
        montarConvidadoDossie(convidado, handleImagemParticipante)
      );
      if (usuarioPodeVerDossie) {
        carregarDossieConvidado(convidado);
      }
    }
  };

  const fecharModal = () => {
    setShowModal(false);
    setItemModal(null);
    setConvidadoDossie(null);
    setIsLoadingDossie(false);
  };

  const dadosModal = useMemo(() => {
    if (!itemModal?.agenda) return null;

    const agenda = itemModal.agenda;
    const idRef = idUsuarioAgenda || usuario?.id;
    const inicio = horarioCurto(agenda.dt_hora_inicio);
    const fim = horarioCurto(agenda.dt_hora_fim);
    const dataIso = extrairDataIso(agenda.dt_hora_inicio);
    const diaEvento = diasEvento.find((item) => item.data === dataIso);
    const diaLabel =
      diaEvento?.label ||
      (formatarDia(agenda.dt_hora_inicio)
        ? formatarDia(agenda.dt_hora_inicio).substring(0, 5).replace("-", "/")
        : diaAtivoLabel);
    const detalhes = agenda.detalhe
      ? String(agenda.detalhe)
          .split(/<br\s*\/?>/i)
          .map((d) => d.trim())
          .filter(Boolean)
      : [];

    let palestrantesModal = [];
    if (
      agenda.tipo === "conteudo_patrocinado" ||
      agenda.tipo === "conteudo_editorial"
    ) {
      if (agenda.convidados) {
        const ids = String(agenda.convidados)
          .split(",")
          .map((i) => parseInt(i, 10))
          .filter((n) => !Number.isNaN(n));
        palestrantesModal = (palestrantes || []).filter((p) =>
          ids.includes(p.id)
        );
      }
    }

    return {
      agenda,
      diaLabel,
      inicio,
      fim,
      detalhes,
      palestrantesModal,
      local: agenda.local || (ehNetworking(agenda.tipo)
        ? "Centro de Convenções Arena Sauípe"
        : "Sala de reuniões"),
      temReuniao: ehTipoReuniao(agenda.tipo),
      mostraDossie: ehTipoReuniao(agenda.tipo) && usuarioPodeVerDossie,
      patrocinadorAgenda: dadosPatrocinadorAgenda(agenda),
      ehConvidado: ehUsuarioConvidado(usuario),
      idRef,
    };
  }, [
    itemModal,
    idUsuarioAgenda,
    usuario,
    usuarioPodeVerDossie,
    palestrantes,
    diasEvento,
    diaAtivoLabel,
  ]);

  return (
    <div className="main">
      <div className="corpo-pagina agenda-page">
        <div className="apl-page">
          <header className="apl-header" aria-label="Cabeçalho da agenda">
            <div className="apl-header-text">
              <h1>Minha agenda</h1>
              <p>
                Confira seus compromissos do evento por dia, incluindo conteúdos
                e reuniões de negócio.
              </p>
            </div>
          </header>

          <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
            {error ? (
              <div className="apl-alert" role="alert">
                {error}
              </div>
            ) : null}

            {!isLoadingDias && diasEvento.length > 0 ? (
              <nav className="apl-day-tabs" aria-label="Dias do evento">
                {diasEvento.map((item) => (
                  <button
                    key={item.data}
                    type="button"
                    className={`apl-day-tab${
                      diaSelecionado === item.dia ? " is-active" : ""
                    }`}
                    onClick={() => setDiaSelecionado(item.dia)}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            ) : null}

            {isLoading || isLoadingDias ? (
              <div className="apl-card">
                <div className="apl-loading">
                  <FaSpinner className="fa-spin" size={28} aria-hidden />
                  <span>
                    {isLoadingDias
                      ? "Carregando dias do evento…"
                      : "Carregando sua agenda…"}
                  </span>
                </div>
              </div>
            ) : (
              <section className="apl-card" aria-label="Lista da minha agenda">
                <div className="apl-table-wrap">
                  {itens.length === 0 ? (
                    <div className="apl-empty">
                      <p className="apl-empty-title">
                        Nenhum compromisso neste dia
                      </p>
                      <p style={{ margin: 0 }}>
                        Selecione outro dia ou confira novamente mais tarde.
                      </p>
                    </div>
                  ) : (
                    <ul className="agenda-list" aria-label="Compromissos do dia">
                      {itens.map((item) => {
                        const agenda = item.agenda;
                        if (!agenda) return null;

                        const inicio = horarioCurto(agenda.dt_hora_inicio);
                        const fim = horarioCurto(agenda.dt_hora_fim);
                        const isMm = agenda.tipo === "m_m";
                        const isNetworking = ehNetworking(agenda.tipo);
                        const isReuniao = ehTipoReuniao(agenda.tipo);
                        const isConvidado = ehUsuarioConvidado(usuario);
                        const mostraStatus = exibeStatusPresenca(agenda.tipo);
                        const idRef = idUsuarioAgenda || usuario?.id;
                        const outro = isReuniao
                          ? outroParticipanteReuniao(
                              agenda,
                              idRef,
                              isConvidado ? null : "convidado"
                            )
                          : null;
                        const patro = isReuniao
                          ? dadosPatrocinadorAgenda(agenda)
                          : null;
                        const avatarLogo =
                          isReuniao && isConvidado && patro?.logo
                            ? patro.logo
                            : null;
                        const avatarPessoa =
                          isReuniao && !avatarLogo && outro
                            ? handleImagemParticipante(outro)
                            : null;
                        const tituloReuniao = isConvidado
                          ? patro?.nome || outro?.empresa || outro?.nome
                          : outro?.nome;
                        const realizado = item.presenca != null;
                        const detalhe = detalheLista(item, usuario, idRef);

                        return (
                          <li key={chaveItemAgenda(item, agenda)}>
                            <button
                              type="button"
                              className={`agenda-item${
                                mostraStatus && realizado ? " is-done" : ""
                              }${isMm ? " is-mm" : ""}${
                                isReuniao ? " is-reuniao" : ""
                              }${isNetworking ? " is-networking" : ""}`}
                              onClick={() => abrirModal(item)}
                            >
                              <div className="agenda-item-time" aria-hidden>
                                <span className="agenda-item-time-start">
                                  {inicio}
                                </span>
                                {fim !== "—" ? (
                                  <span className="agenda-item-time-end">
                                    {fim}
                                  </span>
                                ) : null}
                              </div>

                              <div className="agenda-item-main">
                                <div className="agenda-item-top">
                                  {agenda.tipo ? (
                                    <span
                                      className={`apl-tag-type${
                                        isMm ? " apl-tag-type--mm" : ""
                                      }${
                                        isNetworking
                                          ? " apl-tag-type--networking"
                                          : ""
                                      }`}
                                    >
                                      {LABELS_TIPO[agenda.tipo] || agenda.tipo}
                                    </span>
                                  ) : null}
                                  {mostraStatus ? (
                                    <span
                                      className={`agenda-item-status${
                                        realizado
                                          ? " agenda-item-status--done"
                                          : ""
                                      }`}
                                      aria-label={
                                        realizado
                                          ? "Compromisso realizado"
                                          : "Compromisso pendente"
                                      }
                                    >
                                      {realizado ? (
                                        <>
                                          <FaCheckCircle
                                            size={12}
                                            aria-hidden
                                          />
                                          Realizado
                                        </>
                                      ) : (
                                        <>
                                          <FaRegCircle size={12} aria-hidden />
                                          Pendente
                                        </>
                                      )}
                                    </span>
                                  ) : null}
                                </div>

                                <div className="agenda-item-content">
                                  {avatarLogo || avatarPessoa ? (
                                    <img
                                      className={`agenda-item-avatar${
                                        avatarLogo
                                          ? " agenda-item-avatar--logo"
                                          : ""
                                      }`}
                                      src={avatarLogo || avatarPessoa}
                                      alt=""
                                    />
                                  ) : null}

                                  <div className="agenda-item-body">
                                    <strong className="agenda-item-title">
                                      {isReuniao && tituloReuniao
                                        ? tituloReuniao
                                        : agenda.nome || "—"}
                                    </strong>

                                    <div className="agenda-item-meta">
                                      {detalhe && !isNetworking ? (
                                        <span
                                          className={
                                            isMm
                                              ? "agenda-item-mm"
                                              : "agenda-item-detail"
                                          }
                                        >
                                          {detalhe}
                                        </span>
                                      ) : null}
                                      {agenda.local && !isReuniao ? (
                                        <span className="agenda-item-local">
                                          <FaMapMarkerAlt
                                            size={11}
                                            aria-hidden
                                          />
                                          {agenda.local}
                                        </span>
                                      ) : null}
                                      <span className="agenda-item-duration">
                                        <FaClock size={11} aria-hidden />
                                        {inicio}
                                        {fim !== "—" ? ` – ${fim}` : ""}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {itens.length > 0 ? (
                  <footer className="apl-footer">
                    <span>
                      Exibindo <strong>{itens.length}</strong>{" "}
                      {itens.length === 1 ? "compromisso" : "compromissos"}
                    </span>
                    <span>Dia {diaAtivoLabel}</span>
                  </footer>
                ) : null}
              </section>
            )}
          </div>
        </div>
      </div>

      <ModalB
        show={showModal}
        onHide={fecharModal}
        centered
        size={dadosModal?.mostraDossie ? "xl" : "lg"}
        dialogClassName={[
          "agenda-detalhe-modal",
          dadosModal?.mostraDossie ? "agenda-modal-dossie" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <ModalB.Header closeButton>
          <ModalB.Title className="titulo-modal">
            Detalhe do compromisso
          </ModalB.Title>
        </ModalB.Header>
        <ModalB.Body
          className={
            dadosModal
              ? [
                  dadosModal.agenda?.tipo === "m_m" ? "is-mm" : "",
                  ehNetworking(dadosModal.agenda?.tipo) ? "is-networking" : "",
                  ehTipoReuniao(dadosModal.agenda?.tipo) ? "is-reuniao" : "",
                ]
                  .filter(Boolean)
                  .join(" ") || undefined
              : undefined
          }
        >
          {dadosModal ? (
            <div className="agenda-modal">
              <div className="agenda-modal-meta">
                <div className="agenda-modal-meta-top">
                  {dadosModal.diaLabel ? (
                    <span className="agenda-modal-dia">
                      {dadosModal.diaLabel}
                    </span>
                  ) : null}
                  <span
                    className={`apl-stat${
                      dadosModal.agenda.tipo === "m_m"
                        ? " apl-horario--mm"
                        : ""
                    }`}
                  >
                    {dadosModal.inicio} – {dadosModal.fim}
                  </span>
                  {dadosModal.agenda.tipo ? (
                    <span
                      className={`apl-tag-type${
                        dadosModal.agenda.tipo === "m_m"
                          ? " apl-tag-type--mm"
                          : ""
                      }`}
                    >
                      {LABELS_TIPO[dadosModal.agenda.tipo] ||
                        dadosModal.agenda.tipo}
                    </span>
                  ) : null}
                </div>
                {dadosModal.ehConvidado &&
                dadosModal.temReuniao &&
                (dadosModal.patrocinadorAgenda?.logo ||
                  dadosModal.patrocinadorAgenda?.nome) ? (
                  <div className="agenda-modal-meta-split">
                    {dadosModal.patrocinadorAgenda.logo ? (
                      <img
                        className="agenda-modal-logo"
                        src={dadosModal.patrocinadorAgenda.logo}
                        alt={
                          dadosModal.patrocinadorAgenda.nome || "Patrocinador"
                        }
                      />
                    ) : null}
                    <div className="agenda-modal-meta-info">
                      <h2 className="agenda-modal-title">
                        {dadosModal.patrocinadorAgenda.nome ||
                          convidadoDossie?.nome ||
                          dadosModal.agenda.nome ||
                          "—"}
                      </h2>
                      <p className="agenda-modal-local">
                        <FaMapMarkerAlt size={12} aria-hidden />{" "}
                        {dadosModal.local}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="agenda-modal-identidade">
                      <h2 className="agenda-modal-title">
                        {convidadoDossie?.nome ||
                          dadosModal.agenda.nome ||
                          "—"}
                      </h2>
                      {convidadoDossie?.cargo || convidadoDossie?.empresa ? (
                        <p className="agenda-modal-cargo-empresa">
                          {[convidadoDossie?.cargo, convidadoDossie?.empresa]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      ) : null}
                    </div>
                    <p className="agenda-modal-local">
                      <FaMapMarkerAlt size={12} aria-hidden /> {dadosModal.local}
                    </p>
                  </>
                )}
                {dadosModal.agenda.tipo === "m_m" ? (
                  <p className="agenda-item-mm" style={{ marginTop: "0.35rem" }}>
                    Esta reunião será realizada na plenária!
                  </p>
                ) : null}
              </div>

              {dadosModal.detalhes.length > 0 ? (
                <div className="agenda-modal-detalhes">
                  {dadosModal.detalhes.map((linha, idx) => (
                    <p key={`${idx}-${linha}`}>{linha}</p>
                  ))}
                </div>
              ) : null}

              {(dadosModal.agenda.tipo === "conteudo_patrocinado" ||
                dadosModal.agenda.tipo === "conteudo_editorial") &&
              (dadosModal.agenda.patrocinador?.logo ||
                dadosModal.agenda.patrocinador?.patrocinador) ? (
                <div className="agenda-modal-patrocinador">
                  {dadosModal.agenda.patrocinador?.logo ? (
                    <img
                      src={dadosModal.agenda.patrocinador.logo}
                      alt={
                        dadosModal.agenda.patrocinador.patrocinador ||
                        "Patrocinador"
                      }
                    />
                  ) : (
                    <strong>
                      {dadosModal.agenda.patrocinador.patrocinador}
                    </strong>
                  )}
                </div>
              ) : null}

              {dadosModal.palestrantesModal.length > 0 ? (
                <div className="agenda-modal-palestrantes">
                  <h3>Palestrantes</h3>
                  <ul>
                    {dadosModal.palestrantesModal.map((p) => (
                      <li key={p.id}>
                        <img
                          src={handleImagemParticipante(p)}
                          alt=""
                          className="agenda-modal-avatar"
                        />
                        <div>
                          <strong>{p.nome}</strong>
                          {p.cargo || p.empresa ? (
                            <span className="apl-stat-muted">
                              {[p.cargo, p.empresa].filter(Boolean).join(" · ")}
                            </span>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {dadosModal.mostraDossie ? (
                <div className="agenda-modal-dossie-wrap">
                  {isLoadingDossie && !convidadoDossie ? (
                    <div className="apl-loading">
                      <FaSpinner className="fa-spin" size={22} aria-hidden />
                      <span>Carregando dossiê…</span>
                    </div>
                  ) : convidadoDossie ? (
                    <div className="acu-dossie-preview">
                      <DossieConvidadoSheet
                        convidado={convidadoDossie}
                        embedded
                      />
                    </div>
                  ) : (
                    <p className="apl-stat-muted" style={{ margin: 0 }}>
                      Nenhum convidado vinculado a esta reunião.
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}
        </ModalB.Body>
      </ModalB>
    </div>
  );
};

export default Agenda;
